import { useEffect, useRef, useState } from "react";
import { useAuth } from "src/providers/AuthProvider";
import api from "src/store/features/api";
import {
  setUkRiskSummaryViewed,
  setUkInvestorType,
  setUkRestrictedStatement,
  setUkHnwStatement,
  setUkAssessmentResult,
  setUkAssessmentResultAcknowledged,
  setUkCoolingOff,
  setUkFinalConfirmation,
  setUkSelectedAccountType,
  setUkCheckpoints,
  setUkProgressExpired,
} from "src/store/features/onboarding.slice";
import { useAppDispatch } from "src/store/store";
import { isUkCountry } from "src/utils/isUkCountry";

// Resolves the user's country straight from the v2 user-data query. The user
// slice defaults address.country to "US" and is only hydrated once the Header
// fetch lands, so reading the slice at mount time misclassifies UK users —
// callers must wait for countryReady before trusting isUkUser.
export function useUkUserCountry() {
  const { isAuthenticated } = useAuth();
  const { data, isError } = api.useGetUserDatav2Query(undefined, { skip: !isAuthenticated });
  return {
    isUkUser: isUkCountry(data?.address?.country),
    // If the user-data fetch fails outright, fall back to the non-UK path
    // rather than blocking the page forever.
    countryReady: data !== undefined || isError,
  };
}

// Hydrates UK onboarding state from the backend so returning users land on
// the correct step instead of repeating the assessment. Shared between the
// individual application flow and the entity onboarding questionnaire gate.
// ukStatusReady is only true once the country is known AND (for UK users)
// the backend UK onboarding status has been applied to Redux.
export function useHydrateUkOnboardingStatus() {
  const { isUkUser, countryReady } = useUkUserCountry();
  const dispatch = useAppDispatch();
  const [fetchUkStatus] = api.useLazyGetUkOnboardingStatusQuery();
  const ukStatusFetched = useRef(false);
  const [ukStatusHydrated, setUkStatusHydrated] = useState(false);

  useEffect(() => {
    if (!countryReady) return;
    if (!isUkUser || ukStatusFetched.current) {
      setUkStatusHydrated(true);
      return;
    }
    ukStatusFetched.current = true;

    fetchUkStatus()
      .unwrap()
      .then((status) => {
        // Saved progress lapsed (10 days after step one) and the backend has
        // already reset the re-doable steps — surface why the user is
        // starting again. The status below already reflects the reset state.
        if (status.progressExpired) {
          dispatch(setUkProgressExpired(true));
        }
        // Pause-and-come-back checkpoints
        if (status.selectedAccountType) {
          dispatch(setUkSelectedAccountType(status.selectedAccountType));
        }
        if (status.checkpoints) {
          dispatch(setUkCheckpoints(status.checkpoints));
        }
        if (status.riskSummaryViewed) {
          dispatch(setUkRiskSummaryViewed(true));
        }
        if (status.investorType === "RESTRICTED" || status.investorType === "HIGH_NET_WORTH") {
          dispatch(setUkInvestorType(status.investorType));
        }
        if (status.statementAgreed) {
          if (status.investorType === "RESTRICTED") {
            dispatch(setUkRestrictedStatement({ declarationAgreed: true }));
          } else if (status.investorType === "HIGH_NET_WORTH") {
            dispatch(setUkHnwStatement({ declarationAgreed: true }));
          }
        }
        if (status.assessmentPassed || status.assessmentManualReview) {
          // The manual-review band (score 80-94) clears the gate like a
          // graded pass — the review flag only tailors the copy on the
          // result screen.
          dispatch(
            setUkAssessmentResult({
              status: "passed",
              manualReview: !!status.assessmentManualReview,
            }),
          );
          // A user who only just passed resumes on the "you have passed"
          // screen and continues from there themselves. But once the journey
          // has clearly moved past the assessment (verification, application
          // form or cooling-off underway), re-showing that screen would send
          // a resuming user backwards — auto-acknowledge so routing lands on
          // the real furthest step (e.g. the cooling-off countdown).
          const movedPastAssessment =
            status.checkpoints?.idVerificationDone ||
            status.checkpoints?.applicationFormDone ||
            status.coolingOffComplete ||
            !!status.coolingOffEndsAt;
          if (movedPastAssessment) {
            dispatch(setUkAssessmentResultAcknowledged(true));
          }
        } else if (status.assessmentAttempts >= 3) {
          dispatch(setUkAssessmentResult({ status: "locked_out" }));
        } else if (status.assessmentAttempts > 0) {
          dispatch(
            setUkAssessmentResult({ status: "failed", attemptNumber: status.assessmentAttempts }),
          );
        }
        if (status.coolingOffComplete) {
          dispatch(setUkCoolingOff({ status: "completed" }));
        } else if (status.coolingOffEndsAt) {
          dispatch(setUkCoolingOff({ status: "in_progress", endsAt: status.coolingOffEndsAt }));
        }
        if (status.finalConfirmation) {
          const confirmation = String(status.finalConfirmation).toLowerCase();
          if (confirmation === "confirmed" || confirmation === "declined") {
            dispatch(setUkFinalConfirmation(confirmation));
          }
        }
      })
      .catch(() => {
        // Status fetch failed — proceed with default state
      })
      .finally(() => {
        setUkStatusHydrated(true);
      });
  }, [isUkUser, countryReady]);

  return { isUkUser, ukStatusReady: countryReady && ukStatusHydrated };
}
